import { useEffect, useRef, useState } from "react";
import { go } from "./saas-shell.js";

/**
 * The traction band on the landing page: MRR, and how much data moves through
 * saucer per day and per month.
 *
 * !! EVERY NUMBER BELOW IS FABRICATED PLACEHOLDER COPY. !!
 *
 * Nothing here is read from D1, KV, R2 SQL or the Iceberg rollup, and the
 * revenue figure is not derived from any system of record — there is no
 * payment provider wired up, so revenue is not a quantity saucer measures
 * anywhere. Treat this file as design copy and replace it wholesale before any
 * of it is used as a business claim.
 */

const MRR = "$34,120";

/** Terabytes per day, Aug 1–22 2026, month to date. Aug 1 is a Saturday. */
const DAILY_TB = [
  120, 110, 205, 215, 210, 220, 225, 125, 115, 220, 230,
  225, 235, 240, 135, 125, 235, 245, 240, 250, 255, 140,
];

/** Petabytes per month. The current month is partial and says so. */
const MONTHLY_PB = [
  { month: "Mar", pb: 3.55 },
  { month: "Apr", pb: 4.15 },
  { month: "May", pb: 4.75 },
  { month: "Jun", pb: 5.4 },
  { month: "Jul", pb: 5.95 },
  { month: "Aug", pb: 4.32, partial: true },
];

const CONTACT_EMAIL = "hey@saucer.sh";

const MTD_TB = DAILY_TB.reduce((sum, tb) => sum + tb, 0);
const PER_DAY_TB = MTD_TB / DAILY_TB.length;
/** Bytes per second implied by the month-to-date average. */
const BYTES_PER_SECOND = (MTD_TB * 1e12) / (DAILY_TB.length * 86_400);
/**
 * Quoted in bits, because that is the unit throughput is measured in
 * everywhere it matters — a link, a NIC, a transit bill. The same rate in
 * MB/s reads an order of magnitude smaller than the volume it produces.
 */
const GIGABITS_PER_SECOND = (BYTES_PER_SECOND * 8) / 1e9;

/**
 * `level` exists because this band is the whole of /metrics but only a section
 * of the landing — the page that leads with it needs the h1.
 */
export function Traction({ level = 2 }: { level?: 1 | 2 }) {
  const Heading = level === 1 ? "h1" : "h2";

  return (
    <section className="saas-traction" id="traction">
      <div className="saas-wrap-5xl">
        <span className="saas-chip">Traction</span>
        <Heading className="saas-h2">Revenue, and the weather underneath it</Heading>
        <p className="saas-lede">
          saucer is delivery infrastructure for B2B SaaS teams who ship files to their own
          customers. Two numbers matter: what it earns, and how much it moves.
        </p>

        <div className="tract-headline">
          <Figure kind="money" value={MRR} label="Monthly recurring revenue" />
          <Figure kind="data" value={`${(MTD_TB / 1000).toFixed(2)} PB`} label="Processed in August so far" />
          <Figure kind="data" value={`${PER_DAY_TB.toFixed(0)} TB`} label="Every single day" />
        </div>

        <LiveRate />
        <DailyChart />
        <MonthlyStrip />

        <Brief />
      </div>
    </section>
  );
}

/**
 * The B2B ask: three fields on one full-width line, and a Copy button.
 *
 * The previous version leaned on a `mailto:` link, which does nothing at all
 * on a machine with no mail client registered — the link fires, the OS has no
 * handler, and the page just sits there looking broken. So copying is the
 * primary action: it works everywhere, and a developer pasting into whatever
 * client they actually use is a shorter path than a draft window they did not
 * ask for. The address stays visible next to it, because a contact block that
 * hides the address behind a button is its own kind of broken.
 *
 * There is no form endpoint in this Worker, and a form that POSTs nowhere is
 * worse than no form — hence the clipboard rather than a submit.
 */
const FIELDS = [
  { key: "ship", label: "What you ship", placeholder: "nightly builds" },
  { key: "volume", label: "Per month", placeholder: "40 TB" },
  { key: "customers", label: "To how many", placeholder: "300 customers" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

function Brief() {
  const [answers, setAnswers] = useState<Record<FieldKey, string>>({
    ship: "",
    volume: "",
    customers: "",
  });
  const [copied, setCopied] = useState(false);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  const filled = FIELDS.filter((field) => answers[field.key].trim() !== "");
  const draft = [
    `To: ${CONTACT_EMAIL}`,
    "Subject: Business account enquiry",
    "",
    ...filled.map((field) => `${field.label}: ${answers[field.key].trim()}`),
  ].join("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard permission, or an insecure origin. Select the text so the
      // reader can still take it with the keyboard rather than losing it.
      draftRef.current?.select();
    }
  }

  return (
    <div className="brief">
      <div className="brief__head">
        <span className="saas-chip">B2B only</span>
        <h3 className="brief__title">Move your bytes with ours</h3>
        <p className="brief__note">
          Volume pricing, contracts, security review, migration.
        </p>
      </div>

      <div className="brief__line">
        {FIELDS.map((field) => (
          <label className="brief__field" key={field.key}>
            <span className="brief__label">{field.label}</span>
            <input
              className="brief__input"
              type="text"
              value={answers[field.key]}
              placeholder={field.placeholder}
              onChange={(event) =>
                setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))
              }
            />
          </label>
        ))}

        <button className="brief__cta" type="button" onClick={copy}>
          {copied ? "Copied" : "Copy brief"}
        </button>
      </div>

      <p className="brief__addr">
        Then send it to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Starting on your
        own instead? <a href="/pricing" onClick={go("/pricing")}>Pricing</a> and the{" "}
        <a href="/docs" onClick={go("/docs")}>docs</a> have you covered.
      </p>

      {/* The clipboard fallback needs a real selectable node to reach for. */}
      <textarea ref={draftRef} className="brief__draft" value={draft} readOnly tabIndex={-1} aria-hidden="true" />
    </div>
  );
}

function Figure({ kind, value, label }: { kind: "money" | "data"; value: string; label: string }) {
  return (
    <div className={`tract-fig tract-fig--${kind}`}>
      <span className="tract-fig__value">{value}</span>
      <span className="tract-fig__label">{label}</span>
    </div>
  );
}

/**
 * The live bit. The rate eases along a slow sine around the month-to-date
 * average — a wide, quiet drift rather than a jitter, because a figure that
 * twitches every tick reads as a broken widget, not an instrument. The second
 * line counts real elapsed time since the visitor opened the page, which is
 * the only genuinely live quantity here and the one that makes the scale land.
 */
function LiveRate() {
  const opened = useRef<number | null>(null);
  const [sinceOpen, setSinceOpen] = useState(0);
  const [rate, setRate] = useState(GIGABITS_PER_SECOND);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const tick = () => {
      if (opened.current === null) opened.current = performance.now();
      const elapsed = (performance.now() - opened.current) / 1000;
      setSinceOpen(elapsed * BYTES_PER_SECOND);
      // Two sines of different periods, so the drift never visibly repeats,
      // held to ±3.5% — enough to be alive, not enough to flicker.
      const drift = Math.sin(elapsed / 9) * 0.024 + Math.sin(elapsed / 3.4) * 0.011;
      setRate(GIGABITS_PER_SECOND * (1 + drift));
    };

    const timer = window.setInterval(tick, 120);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="tract-live">
      <span className="tract-live__dot" aria-hidden="true" />
      <span className="tract-live__rate">{rate.toFixed(1)} Gb/s</span>
      <span className="tract-live__sep">right now</span>
      <span className="tract-live__since">
        {sinceOpen > 0
          ? `+${(sinceOpen / 1e9).toFixed(2)} GB since you opened this page`
          : "streaming"}
      </span>
    </div>
  );
}

type Point = { x: number; y: number };

const W = 1000;
const H = 300;

function DailyChart() {
  const [hover, setHover] = useState<number | null>(null);
  const peak = Math.max(...DAILY_TB);
  const floor = 0;
  const step = W / (DAILY_TB.length - 1);

  const points = DAILY_TB.map((tb, i) => ({
    x: i * step,
    y: H - ((tb - floor) / (peak - floor)) * (H - 30) - 15,
  }));

  const line = smooth(points);
  const area = `${line} L ${W} ${H} L 0 ${H} Z`;
  const active = hover === null ? null : points[hover];

  return (
    <figure className="tract-chart">
      <figcaption className="tract-chart__cap">
        <span>Terabytes per day</span>
        <span className="tract-chart__read">
          {hover === null
            ? `Aug 1–${DAILY_TB.length}, peak ${peak} TB`
            : `Aug ${hover + 1} — ${DAILY_TB[hover]} TB`}
        </span>
      </figcaption>

      <div className="tract-chart__plot">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`Daily throughput, ${MTD_TB} terabytes across ${DAILY_TB.length} days of August 2026`}
        >
          <defs>
            <linearGradient id="tract-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.42" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#tract-fill)" />
          <path
            d={line}
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {active && (
            <g>
              <line
                x1={active.x}
                y1={active.y}
                x2={active.x}
                y2={H}
                stroke="var(--line)"
                vectorEffect="non-scaling-stroke"
              />
              <circle cx={active.x} cy={active.y} r="5" fill="var(--accent)" />
            </g>
          )}
        </svg>

        {/* One hit target per day, so the readout works on touch as well. */}
        <div className="tract-chart__hits" onMouseLeave={() => setHover(null)}>
          {DAILY_TB.map((tb, i) => (
            <button
              type="button"
              key={i}
              className="tract-chart__hit"
              aria-label={`August ${i + 1}: ${tb} terabytes`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            />
          ))}
        </div>
      </div>

      <div className="tract-chart__axis">
        <span>Aug 1</span>
        <span>Aug {Math.ceil(DAILY_TB.length / 2)}</span>
        <span>Aug {DAILY_TB.length}</span>
      </div>
    </figure>
  );
}

function MonthlyStrip() {
  const peak = Math.max(...MONTHLY_PB.map((m) => m.pb));

  return (
    <div className="tract-months">
      {MONTHLY_PB.map((m) => (
        <div className={`tract-month${m.partial ? " tract-month--partial" : ""}`} key={m.month}>
          <span className="tract-month__value">{m.pb.toFixed(2)} PB</span>
          <div className="tract-month__track">
            <span className="tract-month__fill" style={{ height: `${(m.pb / peak) * 100}%` }} />
          </div>
          <span className="tract-month__label">
            {m.month}
            {m.partial ? <i> MTD</i> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Catmull-Rom through the points, converted to cubic beziers. */
function smooth(pts: Point[]): string {
  const at = (i: number): Point => pts[Math.min(Math.max(i, 0), pts.length - 1)]!;
  let d = `M ${at(0).x} ${at(0).y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}
